import { useDispatch, useSelector } from 'react-redux';

import type { AuthService } from '@/new_src/service/inbound/Auth/port';

import type { MyPolicy } from 'apis/PolicyAPI';
import { selectCurrentPolicy, selectCurrentUser, sessionActions, sessionSelectors } from 'modules/session';

export const useAuthService = (): AuthService => {
  const dispatch = useDispatch();
  const currentUser: SpenditUser = useSelector(selectCurrentUser);
  const currentUserPolicy = useSelector(selectCurrentPolicy);

  const userPrivatePolicy = useSelector(sessionSelectors.privatePolicy);
  const userMyPolicies = useSelector(sessionSelectors.myPolicies);

  const getCurrentUserId = () => currentUser?.id;
  const getCurrentUserInfo = () => currentUser;
  const getCurrentUserName = () => currentUser.name;
  const getCurrentUserEmail = () => currentUser.email;
  const getCurrentUserCompanyName = () => currentUser.company_name;

  const getUserCurrentPolicy = () => currentUserPolicy;

  const getUserPrivatePolicy = () => userPrivatePolicy;

  const getUserSelectablePolicies = (): MyPolicy[] => {
    const privatePolicy = getUserPrivatePolicy();
    const result = [privatePolicy, ...userMyPolicies.filter(myPolicy => !myPolicy.disabled)];

    if (result[0] === undefined && result.length === 1) return [];

    return result;
  };

  const getCurrentUserThumbnailURL = () => {
    return currentUser.thumbnail_url;
  };

  const getCurrentUserRole = () => {
    return currentUser.role;
  };

  const getSSOAWSCognitoQueryParams = () => {
    /*
     * AWS Cognito Service는 다음 URL형식으로 redirect되어서 들어온다.
     * https://app.spendit.kr/sign_in#access_token={access_token_jwt}&id_token={ID_token_jwt}&token_type=Bearer&expires_in=600
     */
    const location = window.location.href.replace('#', '?');
    const idToken = new URL(location).searchParams.get('id_token');
    return { idToken };
  };
  const isSSOAWSCognitoRequest = () => {
    /* AWS Cognito 를 통해 들어온 요청인지 파악한다. */
    const { idToken } = getSSOAWSCognitoQueryParams();
    return Boolean(idToken);
  };
  const getSSOQueryParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const loginMethod = searchParams.get('login_method');
    const companyId = searchParams.get('company_id');
    const jwt = searchParams.get('jwt') || searchParams.get('kAuth');
    return { loginMethod, companyId, jwt };
  };
  const isSSOLoginRequest = () => {
    const { loginMethod, companyId, jwt } = getSSOQueryParams();
    return Boolean(loginMethod && companyId && jwt);
  };

  /** Don't use Directly */
  const signOutGlobalSession = () => {
    dispatch(sessionActions.signOut());
  };

  const changeUserCurrentPolicy = (targetPolicy: MyPolicy) => {
    dispatch(sessionActions.changeCurrentPolicy({ nextPolicy: targetPolicy }));
  };

  return {
    getCurrentUserId,
    getCurrentUserInfo,
    getCurrentUserThumbnailURL,
    getCurrentUserEmail,
    getCurrentUserCompanyName,
    getCurrentUserName,
    getUserCurrentPolicy,
    getUserPrivatePolicy,
    getUserSelectablePolicies,
    getCurrentUserRole,
    getSSOAWSCognitoQueryParams,
    isSSOAWSCognitoRequest,
    getSSOQueryParams,
    isSSOLoginRequest,
    signOutGlobalSession,
    changeUserCurrentPolicy,
  };
};
